import { useMemo, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { FilterField } from '@/components/ui/FilterBar';
import type { ExportCell } from '../exportReport';
import { Loading, PeriodBar, ReportFrame } from '../reports/reportParts';
import { neg, num, tdate, td, th, useExcel, useKeyNav, usePeriod } from '../reports/reportUtils';
import { fyChoices, monthLabel, statusTone, useFy } from './tdsUtils';

/*
 * One TDS register, as the prototype's report tables: the year (or a period), section and status
 * filters, search, a total of the money columns, Excel and Print; a line with a voucher opens it.
 */

/** d date · m money · m0 money without a total · n count · p percent · mo month · mono code · s status · t text · w wide text */
export type Fmt = 'd' | 'm' | 'm0' | 'n' | 'p' | 'mo' | 'mono' | 's' | 't' | 'w';
export type Col<R> = [keyof R & string, string, Fmt];

/** Dates inside a status or a note read as the rest of the screen (2-Nov-26). */
const withDates = (t: string) => t.replace(/\b\d{4}-\d{2}-\d{2}\b/g, (d) => tdate(d));
const cellText = (v: unknown, f: Fmt) => (v == null || v === '' ? '' : f === 'd' ? tdate(String(v)) : f === 'mo' ? monthLabel(String(v)) : f === 'm' || f === 'm0' ? neg(Number(v)) : f === 'p' ? `${Number(v)}%` : f === 's' || f === 't' || f === 'w' ? withDates(String(v)) : String(v));
const isNum = (f: Fmt) => f === 'm' || f === 'm0' || f === 'n' || f === 'p';

export function TdsRegister<R extends object>({ title, cols, rows, q, open, toolbar, asOn, fyPicker = true, range = false, note, rowKey, actions }: {
  title: string; cols: Col<R>[]; rows: R[]; q: { isLoading: boolean; isError: boolean; error: unknown };
  /** What a line opens (its voucher, a form …). */
  open?: (r: R) => (() => void) | null;
  toolbar?: ReactNode; asOn?: string; fyPicker?: boolean; note?: string; rowKey?: (r: R, i: number) => string;
  /** A From / To period (the account reports' one) instead of the year. */
  range?: boolean;
  /** A last column of buttons (alter, delete …). */
  actions?: (r: R) => ReactNode;
}) {
  const [fy, setFy] = useFy();
  const [p] = usePeriod();
  const [search, setSearch] = useState('');
  const [sec, setSec] = useState('');
  const [st, setSt] = useState('');
  const secCol = cols.find(([k]) => k === 'section');
  const stCol = cols.find(([, , f]) => f === 's');
  const sections = useMemo(() => [...new Set(rows.map((r) => String((r as Record<string, unknown>).section ?? '')).filter(Boolean))].sort(), [rows]);
  const statuses = useMemo(() => (stCol ? [...new Set(rows.map((r) => String((r as Record<string, unknown>)[stCol[0]] ?? '')).filter(Boolean))].sort() : []), [rows, stCol]);
  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter((r) => {
      const x = r as Record<string, unknown>;
      if (sec && x.section !== sec) return false;
      if (st && stCol && x[stCol[0]] !== st) return false;
      return !s || cols.some(([k, , f]) => cellText(x[k], f).toLowerCase().includes(s) || String(x[k] ?? '').toLowerCase().includes(s));
    });
  }, [rows, search, sec, st, cols, stCol]);
  const acts = useMemo(() => shown.map((r) => open?.(r) ?? null), [shown, open]);
  const { row } = useKeyNav(acts);
  const excel = useExcel();
  const totals = cols.map(([k, , f]) => (f === 'm' || f === 'n' ? shown.reduce((s, r) => s + (Number((r as Record<string, unknown>)[k]) || 0), 0) : null));
  const hasTotal = totals.some((x) => x != null);

  return (
    <>
      <PeriodBar noPeriod={!range} onExcel={() => excel(title.replace(/[^A-Za-z0-9]+/g, '_'), title.slice(0, 31), cols.map(([, l]) => l),
        shown.map((r) => cols.map(([k, , f]) => { const v = (r as Record<string, unknown>)[k]; return (isNum(f) ? (v == null ? null : Number(v)) : cellText(v, f)) as ExportCell; })))}>
        {fyPicker && !range && (
          <FilterField label="Year">
            <Select value={fy} onChange={(e) => setFy(e.target.value)}>{fyChoices().map((f) => <option key={f} value={f}>FY {f}</option>)}</Select>
          </FilterField>
        )}
        {secCol && sections.length > 1 && (
          <FilterField label="Section">
            <Select value={sec} onChange={(e) => setSec(e.target.value)}><option value="">All</option>{sections.map((s) => <option key={s}>{s}</option>)}</Select>
          </FilterField>
        )}
        {stCol && statuses.length > 1 && (
          <FilterField label={stCol[1]}>
            <Select value={st} onChange={(e) => setSt(e.target.value)}><option value="">All</option>{statuses.map((s) => <option key={s} value={s}>{withDates(s)}</option>)}</Select>
          </FilterField>
        )}
        <FilterField label="Search" className="col-span-3 sm:w-48"><Input value={search} placeholder="Party, PAN, no. …" onChange={(e) => setSearch(e.target.value)} /></FilterField>
        {toolbar}
      </PeriodBar>
      <Loading q={q}>
        <ReportFrame title={title} right={asOn ? `As on ${tdate(asOn)}` : range ? `${tdate(p.from)} to ${tdate(p.to)}` : `FY ${fy}`} note={note ?? (open ? 'Click a line to open its voucher — or use ↑ ↓ and Enter.' : '')}>
          <table className="w-full text-xs" style={{ minWidth: Math.max(560, cols.length * 105 + (actions ? 120 : 0)) }}>
            <thead>
              <tr className="border-b border-[var(--border-color)]">
                {cols.map(([k, l, f]) => <th key={k} className={clsx(th, isNum(f) ? 'text-right' : 'text-left')}>{l}</th>)}
                {actions && <th className={th} />}
              </tr>
            </thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={rowKey ? rowKey(r, i) : i} {...row(i)}>
                  {cols.map(([k, , f]) => {
                    const v = (r as Record<string, unknown>)[k];
                    const t = cellText(v, f);
                    if (f === 's') {
                      const tone = statusTone(t);
                      return <td key={k} className={td}><span className={clsx('whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-bold', tone === 'ok' ? 'bg-success/15 text-success' : tone === 'bad' ? 'bg-danger/15 text-danger' : 'bg-warning/15 text-warning')}>{t}</span></td>;
                    }
                    return <td key={k} className={clsx(isNum(f) ? num : td, (f === 'mono' || f === 'd') && 'whitespace-nowrap', f === 'mono' && 'font-mono', f === 'w' && 'min-w-[220px] text-[11px] text-[var(--text-secondary)]')}>{t || (isNum(f) ? '' : '—')}</td>;
                  })}
                  {actions && <td className={clsx(td, 'whitespace-nowrap text-right')} onClick={(e) => e.stopPropagation()}>{actions(r)}</td>}
                </tr>
              ))}
              {!shown.length && <tr><td colSpan={cols.length + (actions ? 1 : 0)} className={clsx(td, 'py-6 text-center text-[var(--text-muted)]')}>{rows.length ? 'Nothing matches the filters.' : range ? 'Nothing in this period.' : fyPicker ? 'Nothing for this year.' : 'Nothing entered yet.'}</td></tr>}
            </tbody>
            {hasTotal && shown.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[var(--text-primary)] font-extrabold">
                  {cols.map(([k], i) => <td key={k} className={i === 0 ? td : num}>{i === 0 ? `Grand Total · ${shown.length}` : totals[i] != null ? (cols[i][2] === 'n' ? totals[i] : neg(totals[i]!)) : ''}</td>)}
                  {actions && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </ReportFrame>
      </Loading>
    </>
  );
}
